// components/CallTranscription/index.tsx
// Call Consult - Real-time guidance during P2P calls
// Transcript is internal only - not shown to user

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../../lib/supabase";
import StrengthenAppealModal, { OptionalAppealDetails } from "../Appeals/StrengthenAppealModal";
import { formatAppealDetailsForGenerator } from "../../utils/appealHelpers";

// ============================================
// TYPES
// ============================================

export type RebuttalMode = "standard" | "citation" | "stronger" | "simpler" | "p2p";

interface TranscriptSegment {
  id: string;
  text: string;
  timestamp: number;
  isFinal: boolean;
}

type CallSetupMode = "not_started" | "phone" | "computer" | "ended";

interface KeywordChip {
  id: string;
  value: string;
  source: 'medication' | 'procedure' | 'diagnosis' | 'study' | 'custom';
  isSelected: boolean;
}

export interface CallTranscriptionProps {
  callMode: boolean;
  caseData: {
    id: string;
    friendly_name?: string;
    insurance_company?: string;
    criteria_system?: string;
    diagnoses?: string;
    request_type?: string;
    request_details?: string;
    denial_reason?: string;
    key_facts?: string;
    tried_already?: string;
    additional_context?: string;
  } | null;
  onTranscriptReady?: (transcript: string) => void;
  onProcessTranscript?: (text: string, speaker: string, conversationContext?: string) => void;
  onKeyFactsChange?: (keyFacts: string) => void;
  /** Fires when listening successfully starts */
  onCallStarted?: () => void;
  /** Fires when the call is stopped */
  onCallEnded?: (transcript: string) => void;
  /** Slot rendered between status bar and transcript area (e.g. reviewer authority check) */
  statusBarSlot?: React.ReactNode;
  /** Slot rendered at bottom of card (e.g. QuickTap objection chips) */
  bottomSlot?: React.ReactNode;
}

// ============================================
// KEYWORD GENERATION HELPERS
// ============================================

function extractMedicationName(caseData: CallTranscriptionProps['caseData']): string | null {
  if (!caseData) return null;
  
  const allText = `${caseData.request_details || ''} ${caseData.diagnoses || ''}`;
  
  if (caseData.request_details) {
    const medMatch = caseData.request_details.match(/Medication:\s*([A-Za-z0-9-]+)/i);
    if (medMatch) return medMatch[1];
  }
  
  const knownDrugs = /\b(journavx|suzetrigine|humira|adalimumab|enbrel|etanercept|ozempic|wegovy|mounjaro|semaglutide|tirzepatide|remicade|infliximab|keytruda|pembrolizumab|opdivo|nivolumab|dupixent|dupilumab|skyrizi|risankizumab|rinvoq|upadacitinib|xeljanz|tofacitinib|cosentyx|secukinumab|taltz|ixekizumab|tremfya|guselkumab|stelara|ustekinumab|otezla|apremilast|orencia|abatacept|actemra|tocilizumab|cimzia|certolizumab|simponi|golimumab|rituxan|rituximab|tysabri|natalizumab|entyvio|vedolizumab|xolair|omalizumab|nucala|mepolizumab|fasenra|benralizumab|tezspire|tezepelumab)\b/i;
  
  const knownMatch = allText.match(knownDrugs);
  if (knownMatch) return knownMatch[1];
  
  const drugSuffixPattern = /\b([A-Z][a-z]+(?:mab|nib|zumab|ximab|umab|tinib|ciclib|parib|lisib|rafenib|metinib|citinib|glutide|glifozin|gliptin|prazole|tidine|sartan|pril|statin|olol|lukast|setron|tadine))\b/;
  
  const suffixMatch = allText.match(drugSuffixPattern);
  if (suffixMatch) return suffixMatch[1];
  
  return null;
}

function generateKeywordSuggestions(caseData: CallTranscriptionProps['caseData']): KeywordChip[] {
  const chips: KeywordChip[] = [];
  if (!caseData) return chips;
  
  const seenValues = new Set<string>();
  
  const addChip = (value: string, source: KeywordChip['source']) => {
    const normalizedValue = value.trim();
    const key = normalizedValue.toLowerCase();
    if (normalizedValue.length >= 3 && !seenValues.has(key)) {
      seenValues.add(key);
      chips.push({
        id: `${source}-${chips.length}`,
        value: normalizedValue,
        source,
        isSelected: true,
      });
    }
  };
  
  const medName = extractMedicationName(caseData);
  if (medName) {
    addChip(medName, 'medication');
    
    const brandGenericPairs: Record<string, string> = {
      'journavx': 'suzetrigine',
      'suzetrigine': 'journavx',
      'humira': 'adalimumab',
      'adalimumab': 'humira',
      'enbrel': 'etanercept',
      'etanercept': 'enbrel',
      'ozempic': 'semaglutide',
      'semaglutide': 'ozempic',
      'wegovy': 'semaglutide',
      'mounjaro': 'tirzepatide',
      'tirzepatide': 'mounjaro',
      'remicade': 'infliximab',
      'infliximab': 'remicade',
      'keytruda': 'pembrolizumab',
      'pembrolizumab': 'keytruda',
      'opdivo': 'nivolumab',
      'nivolumab': 'opdivo',
    };
    const pair = brandGenericPairs[medName.toLowerCase()];
    if (pair) {
      addChip(pair, 'medication');
    }
  }
  
  const requestDetails = caseData.request_details || '';
  
  const mriMatch = requestDetails.match(/MRI\s+(?:of\s+(?:the\s+)?)?([A-Za-z\s]+?)(?:\s+(?:without|with|w\/o|w\/)|\s*$|,)/i);
  if (mriMatch) {
    const region = mriMatch[1].trim();
    addChip(`MRI ${region}`, 'study');
    if (region.length >= 3) {
      addChip(region, 'procedure');
    }
  }
  
  const ctMatch = requestDetails.match(/CT\s+(?:scan\s+)?(?:of\s+(?:the\s+)?)?([A-Za-z\s]+?)(?:\s+(?:without|with|w\/o|w\/)|\s*$|,)/i);
  if (ctMatch) {
    const region = ctMatch[1].trim();
    addChip(`CT ${region}`, 'study');
  }
  
  const procedurePatterns = [
    /(?:procedure|surgery|injection|infusion|therapy):\s*([A-Za-z\s\-]+)/i,
    /(?:requesting|request for)\s+([A-Za-z\s\-]+(?:injection|surgery|procedure|therapy))/i,
  ];
  for (const pattern of procedurePatterns) {
    const match = requestDetails.match(pattern);
    if (match) {
      addChip(match[1].trim(), 'procedure');
    }
  }
  
  const diagnoses = (caseData.diagnoses || '').toLowerCase();
  const medicalTerms = [
    { term: 'radiculopathy', display: 'radiculopathy' },
    { term: 'stenosis', display: 'stenosis' },
    { term: 'herniation', display: 'herniation' },
    { term: 'neuropathy', display: 'neuropathy' },
    { term: 'spondylosis', display: 'spondylosis' },
    { term: 'spondylolisthesis', display: 'spondylolisthesis' },
    { term: 'sciatica', display: 'sciatica' },
    { term: 'fibromyalgia', display: 'fibromyalgia' },
    { term: 'osteoarthritis', display: 'osteoarthritis' },
    { term: 'rheumatoid', display: 'rheumatoid arthritis' },
    { term: 'ankylosing', display: 'ankylosing spondylitis' },
    { term: 'cauda equina', display: 'cauda equina' },
    { term: 'myelopathy', display: 'myelopathy' },
    { term: 'degenerative disc', display: 'degenerative disc disease' },
  ];
  
  for (const { term, display } of medicalTerms) {
    if (diagnoses.includes(term)) {
      addChip(display, 'diagnosis');
    }
  }
  
  const additionalContext = (caseData.additional_context || '').toLowerCase();
  const triedAlready = (caseData.tried_already || '').toLowerCase();
  const allText = `${requestDetails} ${diagnoses} ${additionalContext} ${triedAlready}`.toLowerCase();
  
  const abbreviations = [
    { abbr: 'esi', full: 'epidural steroid injection' },
    { abbr: 'snrb', full: 'selective nerve root block' },
    { abbr: 'mbb', full: 'medial branch block' },
    { abbr: 'rfa', full: 'radiofrequency ablation' },
    { abbr: 'tens', full: 'TENS unit' },
    { abbr: 'dmard', full: 'DMARD' },
    { abbr: 'nsaid', full: 'NSAID' },
  ];
  
  for (const { abbr, full } of abbreviations) {
    if (allText.includes(abbr)) {
      addChip(full, 'procedure');
    }
  }
  
  return chips.slice(0, 8);
}

function extractKeyFactsFromCase(caseData: CallTranscriptionProps['caseData']): string {
  if (!caseData) return '';
  
  if (caseData.key_facts && caseData.key_facts.trim()) {
    return caseData.key_facts;
  }
  
  const facts: string[] = [];
  
  const triedAlready = caseData.tried_already || '';
  const additionalContext = caseData.additional_context || '';
  const allText = `${triedAlready} ${additionalContext}`;
  
  const durationMatch = allText.match(/(\d+)\s*(weeks?|months?|years?)/i);
  if (durationMatch) {
    facts.push(`Symptoms for ${durationMatch[1]} ${durationMatch[2]}`);
  }
  
  if (triedAlready) {
    const treatments = triedAlready.split(/[,;]/).filter(t => t.trim()).length;
    if (treatments > 0) {
      facts.push(`Failed ${treatments} prior treatment${treatments > 1 ? 's' : ''}`);
    }
  }
  
  return facts.join('. ');
}

// ============================================
// TRANSCRIPTION HELPERS
// ============================================

function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];
  
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  
  return matrix[b.length][a.length];
}

function soundex(word: string): string {
  const a = word.toLowerCase().split('');
  const firstLetter = a[0];
  
  const codes: Record<string, string> = {
    a: '', e: '', i: '', o: '', u: '', y: '', h: '', w: '',
    b: '1', f: '1', p: '1', v: '1',
    c: '2', g: '2', j: '2', k: '2', q: '2', s: '2', x: '2', z: '2',
    d: '3', t: '3',
    l: '4',
    m: '5', n: '5',
    r: '6'
  };
  
  const coded = a
    .map(char => codes[char] || '')
    .join('')
    .replace(/(.)\1+/g, '$1');
  
  return (firstLetter + coded.slice(1) + '000').slice(0, 4).toUpperCase();
}

function phoneticallyMatch(word1: string, word2: string): boolean {
  const s1 = soundex(word1);
  const s2 = soundex(word2);
  if (s1 === s2) return true;
  if (s1.slice(0, 3) === s2.slice(0, 3)) return true;
  return false;
}

function correctKeywordTranscription(
  text: string,
  keywords: string[]
): string {
  if (!keywords || keywords.length === 0) {
    return text;
  }
  
  const words = text.split(/(\s+)/);
  
  const correctedWords = words.map(word => {
    if (/^\s*$/.test(word) || word.length < 4) return word;
    
    const cleanWord = word.replace(/[.,!?;:'"]+$/, '');
    const punctuation = word.slice(cleanWord.length);
    const wordLower = cleanWord.toLowerCase();
    
    for (const keyword of keywords) {
      if (keyword.length < 4) continue;
      
      const keywordLower = keyword.toLowerCase();
      
      if (wordLower === keywordLower) {
        return keyword + punctuation;
      }
      
      const lengthDiff = Math.abs(cleanWord.length - keyword.length);
      if (lengthDiff > 4) continue;
      
      const distance = levenshteinDistance(wordLower, keywordLower);
      const maxLen = Math.max(cleanWord.length, keyword.length);
      const similarity = 1 - (distance / maxLen);
      
      const phoneticMatch = phoneticallyMatch(cleanWord, keyword);
      
      const sameStart = wordLower.slice(0, 3) === keywordLower.slice(0, 3);
      const shouldCorrect = 
        similarity > 0.50 || 
        (phoneticMatch && similarity > 0.30) ||
        (sameStart && similarity > 0.40);
      
      if (shouldCorrect) {
        console.log(`[KeywordCorrection] "${cleanWord}" -> "${keyword}" (sim=${(similarity * 100).toFixed(0)}%)`);
        return keyword + punctuation;
      }
    }
    
    return word;
  });
  
  return correctedWords.join('');
}

function buildKeywordsParam(keywords: string[]): string {
  if (keywords.length === 0) return '';
  
  return keywords
    .map(kw => `keywords=${encodeURIComponent(kw)}:2`)
    .join('&');
}

// ============================================
// ICONS
// ============================================

const IconMicrophone = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
  </svg>
);

const IconStop = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <rect x="6" y="6" width="12" height="12" rx="1" />
  </svg>
);

const IconPause = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <rect x="6" y="5" width="4" height="14" rx="1" />
    <rect x="14" y="5" width="4" height="14" rx="1" />
  </svg>
);

const IconPlay = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5.14v14l11-7-11-7z" />
  </svg>
);

const IconCopy = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
  </svg>
);

const IconCheck = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

const IconLoader = ({ className }: { className?: string }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const IconX = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const IconPaperclip = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
  </svg>
);

const IconFileText = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

const IconSparkles = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

const IconPhone = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
  </svg>
);

const IconComputer = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
  </svg>
);

const IconBookOpen = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
  </svg>
);

const IconZap = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
  </svg>
);

const IconMinimize = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
  </svg>
);

const IconUsers = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);

const IconMessageQuote = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
  </svg>
);

const IconAlertCircle = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
  </svg>
);

const IconLightbulb = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
  </svg>
);

const IconDocument = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

const IconShieldCheck = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
  </svg>
);

const IconPlus = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const IconArrowRight = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
  </svg>
);

// ============================================
// HELPER
// ============================================

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// ============================================
// MODE BUTTON
// ============================================

function ModeButton({ 
  icon, 
  label, 
  mode, 
  activeMode, 
  isGenerating, 
  onClick 
}: {
  icon: React.ReactNode;
  label: string;
  mode: RebuttalMode;
  activeMode: RebuttalMode;
  isGenerating: boolean;
  onClick: () => void;
}) {
  const isActive = activeMode === mode;
  return (
    <button
      onClick={onClick}
      disabled={isGenerating && isActive}
      className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
        ${isActive
          ? "bg-slate-600 text-white border border-slate-500"
          : "bg-slate-700/50 text-slate-300 border border-slate-600/50 hover:bg-slate-700 hover:border-slate-500"
        } disabled:opacity-50`}
    >
      {isGenerating && isActive ? <IconLoader className="w-4 h-4" /> : icon}
      <span>{label}</span>
    </button>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function CallTranscription({
  callMode,
  caseData,
  onProcessTranscript,
  onKeyFactsChange,
  onCallStarted,
  onCallEnded,
  statusBarSlot,
  bottomSlot,
}: CallTranscriptionProps) {
  
  // ============================================
  // STATE
  // ============================================
  
  const [isConnecting, setIsConnecting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [callSetupMode, setCallSetupMode] = useState<CallSetupMode>("not_started");
  const [selectedAudioSource, setSelectedAudioSource] = useState<"phone" | "computer">("phone");
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const [audioLevel, setAudioLevel] = useState(0);

  // ============================================
  // SETUP SCREEN STATE
  // ============================================
  
  const [showKeywordsField, setShowKeywordsField] = useState(false);
  
  const keywordSuggestions = useMemo(() => generateKeywordSuggestions(caseData), [caseData]);
  
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(() => 
    new Set(keywordSuggestions.filter(k => k.isSelected).map(k => k.value))
  );
  const [customKeyword, setCustomKeyword] = useState('');
  const [customKeywords, setCustomKeywords] = useState<string[]>([]);
  
  const initialKeyFacts = useMemo(() => extractKeyFactsFromCase(caseData), [caseData]);
  const [keyFacts, setKeyFacts] = useState(initialKeyFacts);
  const [showKeyFactsInput, setShowKeyFactsInput] = useState(false);
  const [showCallTuning, setShowCallTuning] = useState(false);
  
  const customKeywordInputRef = useRef<HTMLInputElement>(null);
  
  const activeKeywords = useMemo(() => {
    return [...selectedKeywords, ...customKeywords.filter(k => selectedKeywords.has(k))];
  }, [selectedKeywords, customKeywords]);

  // ============================================
  // REFS - CRITICAL FIX: Include onProcessTranscript ref
  // ============================================
  const caseDataRef = useRef(caseData);
  const keywordsRef = useRef(activeKeywords);
  const onProcessTranscriptRef = useRef(onProcessTranscript);
  const onCallStartedRef = useRef(onCallStarted);
  
  useEffect(() => {
    caseDataRef.current = caseData;
    keywordsRef.current = activeKeywords;
    onProcessTranscriptRef.current = onProcessTranscript;
    onCallStartedRef.current = onCallStarted;
  }, [caseData, activeKeywords, onProcessTranscript, onCallStarted]);

  // ============================================
  // APPEAL LETTER STATE
  // ============================================
  
  const [appealLetter, setAppealLetter] = useState<string | null>(null);
  const [generatingAppeal, setGeneratingAppeal] = useState(false);
  const [appealError, setAppealError] = useState<string | null>(null);
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [appealCopied, setAppealCopied] = useState(false);
  const [additionalContext, setAdditionalContext] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ============================================
  // STRENGTHEN APPEAL MODAL STATE
  // ============================================
  const [showStrengthenModal, setShowStrengthenModal] = useState(false);

  const keyFactsDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // ============================================
  // REFS
  // ============================================
  
  const wsRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const segmentId = useRef(0);
  const isListeningRef = useRef(false);
  const accumulatedTextRef = useRef("");
  const elapsedTimeRef = useRef(0);
  const startTimeRef = useRef(0);
  const lastInterimProcessedRef = useRef("");

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    setKeyFacts(caseData?.key_facts || initialKeyFacts);
  }, [caseData?.id, caseData?.key_facts, initialKeyFacts]);

  useEffect(() => {
    const newSuggestions = generateKeywordSuggestions(caseData);
    setSelectedKeywords(new Set(newSuggestions.filter(k => k.isSelected).map(k => k.value)));
    setShowKeywordsField(false);
  }, [caseData?.id]);

  const handleKeyFactsChange = useCallback((value: string) => {
    setKeyFacts(value);
    
    if (keyFactsDebounceRef.current) {
      clearTimeout(keyFactsDebounceRef.current);
    }
    keyFactsDebounceRef.current = setTimeout(() => {
      onKeyFactsChange?.(value);
    }, 1000);
  }, [onKeyFactsChange]);

  useEffect(() => {
    return () => {
      if (keyFactsDebounceRef.current) {
        clearTimeout(keyFactsDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (copied) {
      const t = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(t);
    }
  }, [copied]);

  useEffect(() => {
    return () => {
      stopListening();
    };
  }, []);

  // ============================================
  // KEYWORD HANDLERS
  // ============================================
  
  const toggleKeyword = (value: string) => {
    setSelectedKeywords(prev => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };
  
  const addCustomKeyword = () => {
    const trimmed = customKeyword.trim();
    if (trimmed && !customKeywords.includes(trimmed) && !selectedKeywords.has(trimmed)) {
      setCustomKeywords(prev => [...prev, trimmed]);
      setSelectedKeywords(prev => new Set([...prev, trimmed]));
      setCustomKeyword('');
    }
  };
  
  const removeCustomKeyword = (value: string) => {
    setCustomKeywords(prev => prev.filter(k => k !== value));
    setSelectedKeywords(prev => {
      const next = new Set(prev);
      next.delete(value);
      return next;
    });
  };
  
  const handleCustomKeywordKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomKeyword();
    }
  };
  
  const handleRevealKeywords = () => {
    setShowKeywordsField(true);
    setTimeout(() => {
      customKeywordInputRef.current?.focus();
    }, 100);
  };

  // ============================================
  // TRANSCRIPTION HANDLERS
  // ============================================

  const getToken = async (): Promise<string | null> => {
    try {
      const { data, error } = await supabase.functions.invoke("deepgram-token");
      if (error) throw error;
      return data?.token || null;
    } catch (e) {
      console.error("[Transcription] Token error:", e);
      return null;
    }
  };

  const getFullTranscript = useCallback((): string => {
    return segments.map(s => s.text).join(" ");
  }, [segments]);

  const startAudioLevelMonitoring = (stream: MediaStream) => {
    try {
      const audioCtx = new AudioContext();
      audioContextRef.current = audioCtx;
      
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      
      source.connect(analyser);
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      const updateLevel = () => {
        if (!analyserRef.current) return;
        
        analyserRef.current.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(Math.min(1, avg / 128));
        
        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };
      
      updateLevel();
    } catch (e) {
      console.warn("[Transcription] Audio level monitoring failed:", e);
    }
  };

  const stopAudioLevelMonitoring = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  };

  const startListening = async (mode: CallSetupMode) => {
    console.log("[Transcription] Starting with mode:", mode);
    setCallSetupMode(mode);
    setIsConnecting(true);
    setErrorMessage(null);
    accumulatedTextRef.current = "";
    lastInterimProcessedRef.current = "";

    try {
      const token = await getToken();
      if (!token) throw new Error("Failed to get token");

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { 
          echoCancellation: true, 
          noiseSuppression: true,
          sampleRate: 16000
        }
      });
      streamRef.current = stream;

      startAudioLevelMonitoring(stream);

      const model = mode === "phone" ? "nova-2-phonecall" : "nova-2-medical";
      const keywordsParam = buildKeywordsParam(activeKeywords);
      
      let wsUrl = `wss://api.deepgram.com/v1/listen?model=${model}&punctuate=true&smart_format=true&interim_results=true&endpointing=1000&utterance_end_ms=2000`;
      if (keywordsParam) {
        wsUrl += `&${keywordsParam}`;
        console.log("[Transcription] Boosting keywords:", activeKeywords);
      }
      
      console.log("[Transcription] Connecting to Deepgram...");
      const ws = new WebSocket(wsUrl, ["token", token]);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("[Transcription] WebSocket connected!");
        setIsConnecting(false);
        setIsListening(true);
        isListeningRef.current = true;
        onCallStartedRef.current?.();

        startTimeRef.current = Date.now();
        elapsedTimeRef.current = 0;
        setElapsedTime(0);
        timerRef.current = setInterval(() => {
          const newElapsed = elapsedTimeRef.current + Math.floor((Date.now() - startTimeRef.current) / 1000);
          setElapsedTime(newElapsed);
        }, 1000);

        const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : MediaRecorder.isTypeSupported("audio/webm")
            ? "audio/webm"
            : "audio/mp4";
        
        console.log("[Transcription] Using MIME type:", mimeType);

        const mediaRecorder = new MediaRecorder(stream, {
          mimeType,
          audioBitsPerSecond: 16000,
        });
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0 && ws.readyState === WebSocket.OPEN) {
            ws.send(event.data);
          }
        };

        mediaRecorder.start(250);
        console.log("[Transcription] MediaRecorder started");
      };

      // ============================================
      // WEBSOCKET MESSAGE HANDLER
      // ============================================
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === "Results") {
            const alt = data.channel?.alternatives?.[0];
            const rawText = alt?.transcript;
            
            if (rawText && rawText.trim()) {
              const text = correctKeywordTranscription(rawText, keywordsRef.current);
              
              if (data.is_final) {
                accumulatedTextRef.current = (accumulatedTextRef.current + " " + text).trim();
                lastInterimProcessedRef.current = "";
                
                setSegments(prev => [...prev, { 
                  id: `seg-${segmentId.current++}`, 
                  text, 
                  timestamp: Date.now(), 
                  isFinal: true 
                }]);
                
                if (onProcessTranscriptRef.current) {
                  const fullContext = accumulatedTextRef.current;
                  const recentContext = fullContext.length > 1000 
                    ? "..." + fullContext.slice(-1000) 
                    : fullContext;
                  console.log("[Transcription] Calling processTranscript (final):", text.substring(0, 50));
                  onProcessTranscriptRef.current(text, "insurance", recentContext);
                }
              } else {
                const wordCount = text.trim().split(/\s+/).length;
                if (wordCount >= 4 && text !== lastInterimProcessedRef.current) {
                  const lastWords = lastInterimProcessedRef.current.trim().split(/\s+/).length;
                  if (wordCount > lastWords + 2 || !text.startsWith(lastInterimProcessedRef.current.trim())) {
                    lastInterimProcessedRef.current = text;
                    if (onProcessTranscriptRef.current) {
                      const fullContext = accumulatedTextRef.current + " " + text;
                      const recentContext = fullContext.length > 1000 
                        ? "..." + fullContext.slice(-1000) 
                        : fullContext;
                      console.log("[Transcription] Calling processTranscript (interim):", text.substring(0, 50));
                      onProcessTranscriptRef.current(text, "insurance", recentContext);
                    }
                  }
                }
              }
            }
          }
        } catch (e) {
          console.error("[Transcription] Parse error:", e);
        }
      };

      ws.onerror = (error) => {
        console.error("[Transcription] WebSocket error:", error);
        setErrorMessage("Connection error. Please try again.");
        stopListening();
      };

      ws.onclose = (event) => {
        console.log("[Transcription] WebSocket closed:", event.code, event.reason);
        if (isListeningRef.current && event.code !== 1000) {
          setErrorMessage("Connection lost. Please restart.");
        }
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setIsListening(false);
        isListeningRef.current = false;
      };

    } catch (error: unknown) {
      console.error("[Transcription] Start error:", error);
      const err = error as Error & { name?: string };
      if (err.name === "NotAllowedError") {
        setErrorMessage("Microphone access denied. Please allow and try again.");
      } else {
        setErrorMessage(err.message || "Failed to start listening.");
      }
      setIsConnecting(false);
    }
  };

  const stopListening = useCallback(() => {
    console.log("[Transcription] Stopping...");
    isListeningRef.current = false;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    stopAudioLevelMonitoring();

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setTimeout(() => {
      if (wsRef.current) {
        wsRef.current.close(1000, "User stopped");
        wsRef.current = null;
      }
    }, 500);

    setIsListening(false);
    setIsConnecting(false);
    setIsPaused(false);
  }, []);

  // Wrapper for the stop button — stops listening then notifies parent
  const handleStopAndNotify = useCallback(() => {
    const transcript = accumulatedTextRef.current;
    stopListening();
    onCallEnded?.(transcript);
  }, [stopListening, onCallEnded]);

  const pauseListening = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(track => {
        track.enabled = false;
      });
      
      elapsedTimeRef.current = elapsedTimeRef.current + Math.floor((Date.now() - startTimeRef.current) / 1000);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      setIsPaused(true);
      console.log("[Transcription] Paused");
    }
  }, []);

  const resumeListening = useCallback(() => {
    if (streamRef.current) {
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        console.error("[Transcription] WebSocket closed during pause");
        setErrorMessage("Connection lost during pause. Please start a new call.");
        setCallSetupMode("ended");
        return;
      }
      
      if (mediaRecorderRef.current?.state !== "recording") {
        console.error("[Transcription] MediaRecorder stopped during pause");
        setErrorMessage("Recording stopped during pause. Please start a new call.");
        setCallSetupMode("ended");
        return;
      }
      
      streamRef.current.getAudioTracks().forEach(track => {
        track.enabled = true;
      });
      
      lastInterimProcessedRef.current = "";
      
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        const newElapsed = elapsedTimeRef.current + Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsedTime(newElapsed);
      }, 1000);
      
      setIsPaused(false);
      console.log("[Transcription] Resumed");
    }
  }, []);

  const reset = () => {
    stopListening();
    setSegments([]);
    setElapsedTime(0);
    elapsedTimeRef.current = 0;
    startTimeRef.current = 0;
    lastInterimProcessedRef.current = "";
    setCallSetupMode("not_started");
    setErrorMessage(null);
    setAppealLetter(null);
    setAppealError(null);
    setAdditionalContext("");
    setUploadedFiles([]);
    accumulatedTextRef.current = "";
  };

  // ============================================
  // APPEAL LETTER HANDLERS
  // ============================================

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'text/plain'];
    const validFiles = files.filter(f => allowedTypes.includes(f.type) || f.name.endsWith('.pdf'));
    setUploadedFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const generateAppealLetter = async (extraContext?: string) => {
    const transcript = getFullTranscript();
    if (!transcript.trim()) {
      setAppealError("No conversation detected. Please complete a call first.");
      return;
    }

    setGeneratingAppeal(true);
    setAppealError(null);

    try {
      const documents = await Promise.all(
        uploadedFiles.map(async (file) => ({
          name: file.name,
          type: file.type,
          data: await fileToBase64(file)
        }))
      );

      const combinedContext = [additionalContext.trim(), extraContext].filter(Boolean).join("\n\n");

      const { data, error } = await supabase.functions.invoke("generate-appeal", {
        body: { 
          transcript, 
          caseData: caseData || {},
          additionalContext: combinedContext || undefined,
          documents: documents.length > 0 ? documents : undefined
        },
      });

      if (error) throw new Error(error.message || "Failed to generate appeal letter");
      if (data?.appeal_letter) {
        setAppealLetter(data.appeal_letter);
        setShowAppealModal(true);
      } else {
        throw new Error("No appeal letter returned");
      }
    } catch (error: unknown) {
      console.error("[Transcription] Appeal generation error:", error);
      setAppealError((error as Error).message || "Failed to generate appeal letter");
    } finally {
      setGeneratingAppeal(false);
    }
  };

  // ============================================
  // STRENGTHEN APPEAL MODAL HANDLERS
  // ============================================

  const handleGenerateClick = () => {
    setShowStrengthenModal(true);
  };

  const handleGenerateWithDetails = async (details: OptionalAppealDetails) => {
    setShowStrengthenModal(false);
    const detailsText = formatAppealDetailsForGenerator(details);
    await generateAppealLetter(detailsText);
  };

  const handleSkipStrengthen = () => {
    setShowStrengthenModal(false);
    generateAppealLetter();
  };

  const copyAppealLetter = async () => {
    if (!appealLetter) return;
    await navigator.clipboard.writeText(appealLetter);
    setAppealCopied(true);
    setTimeout(() => setAppealCopied(false), 2000);
  };

  const hasTranscript = segments.length > 0;
  const callEnded = !isListening && !isConnecting && callSetupMode !== "not_started";
  const selectedCount = selectedKeywords.size;

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className={`rounded-lg border transition-all duration-200 overflow-hidden ${
      callMode ? "bg-slate-800/50 border-slate-700/50" : "bg-white border-slate-200"
    }`}>
      {/* Header — only during active call */}
      {isListening && (
      <div className={`px-4 py-3 border-b flex items-center justify-between ${
        callMode ? "border-slate-700 bg-slate-800/30" : "border-slate-100 bg-slate-50"
      }`}>
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${callMode ? "bg-amber-500/20" : "bg-amber-100"}`}>
            <IconSparkles className={`w-4 h-4 ${callMode ? "text-amber-400" : "text-amber-600"}`} />
          </div>
          <div>
            <h3 className={`text-sm font-medium ${callMode ? "text-slate-200" : "text-slate-700"}`}>
              Call Consult
            </h3>
            <p className={`text-xs ${callMode ? "text-slate-500" : "text-slate-400"}`}>
              Real-time guidance during your call
            </p>
          </div>
        </div>

        {/* Timer and controls */}
        <div className="flex items-center gap-2">
            <div className={`flex items-center rounded-full overflow-hidden ${
              callMode ? "bg-slate-700/80" : "bg-slate-100"
            }`}>
              <div className="flex items-center gap-2 px-3 py-1.5">
                <div className={`w-2 h-2 rounded-full ${
                  isPaused ? "bg-amber-400" : "bg-red-500 animate-pulse"
                }`} />
                <span className={`text-sm font-medium tabular-nums ${
                  callMode ? "text-slate-200" : "text-slate-700"
                }`}>
                  {formatTime(elapsedTime)}
                </span>
              </div>

              <div className={`w-px h-5 ${callMode ? "bg-slate-600" : "bg-slate-300"}`} />

              <button
                onClick={isPaused ? resumeListening : pauseListening}
                className={`p-2 transition-colors ${
                  callMode 
                    ? "text-slate-300 hover:text-teal-400 hover:bg-slate-600/50" 
                    : "text-slate-600 hover:text-teal-600 hover:bg-slate-200"
                }`}
                title={isPaused ? "Resume" : "Pause"}
              >
                {isPaused ? <IconPlay className="w-5 h-5" /> : <IconPause className="w-5 h-5" />}
              </button>

              <div className={`w-px h-5 ${callMode ? "bg-slate-600" : "bg-slate-300"}`} />

              <button
                onClick={handleStopAndNotify}
                className={`p-2 transition-colors ${
                  callMode 
                    ? "text-slate-300 hover:text-red-400 hover:bg-red-500/20" 
                    : "text-slate-600 hover:text-red-600 hover:bg-red-50"
                }`}
                title="End call"
              >
                <IconStop className="w-5 h-5" />
              </button>
            </div>
        </div>
      </div>
      )}

      {/* Error */}
      {errorMessage && (
        <div className={`px-4 py-2 text-sm ${callMode ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"}`}>
          {errorMessage}
        </div>
      )}

      {/* Content */}
      <div className="p-4" style={{ minHeight: isListening ? "300px" : undefined }}>
        
        {/* SETUP SCREEN */}
        {callSetupMode === "not_started" && (
          <div>
            {/* Audio keyframe animations */}
            <style dangerouslySetInnerHTML={{ __html: `
              @keyframes eqBar1 { 0%,100% { height: 5px; } 50% { height: 10px; } }
              @keyframes eqBar2 { 0%,100% { height: 9px; } 50% { height: 4px; } }
              @keyframes eqBar3 { 0%,100% { height: 12px; } 50% { height: 6px; } }
            `}} />

            {/* Pre-Call Briefing */}
            {caseData && (caseData.request_details || caseData.diagnoses || caseData.insurance_company || caseData.denial_reason) && (
              <div className={`rounded-lg border overflow-hidden mb-4 ${
                callMode ? "border-slate-700/30 bg-slate-900/40" : "border-slate-200 bg-slate-50"
              }`}>
                {caseData.request_details && (
                  <div className={`flex items-baseline gap-2 px-3.5 py-2.5 ${
                    callMode ? "border-b border-slate-700/20" : "border-b border-slate-100"
                  }`}>
                    <span className={`text-[11px] font-semibold min-w-[72px] flex-shrink-0 ${
                      callMode ? "text-slate-500" : "text-slate-400"
                    }`}>Requesting</span>
                    <span className={`text-[13px] font-medium ${
                      callMode ? "text-slate-200" : "text-slate-700"
                    }`}>{caseData.request_details}</span>
                  </div>
                )}
                {caseData.diagnoses && (
                  <div className={`flex items-baseline gap-2 px-3.5 py-2.5 ${
                    callMode ? "border-b border-slate-700/20" : "border-b border-slate-100"
                  }`}>
                    <span className={`text-[11px] font-semibold min-w-[72px] flex-shrink-0 ${
                      callMode ? "text-slate-500" : "text-slate-400"
                    }`}>Diagnosis</span>
                    <span className={`text-[13px] font-medium ${
                      callMode ? "text-slate-200" : "text-slate-700"
                    }`}>{caseData.diagnoses}</span>
                  </div>
                )}
                {caseData.insurance_company && (
                  <div className={`flex items-baseline gap-2 px-3.5 py-2.5 ${
                    caseData.denial_reason
                      ? callMode ? "border-b border-slate-700/20" : "border-b border-slate-100"
                      : ""
                  }`}>
                    <span className={`text-[11px] font-semibold min-w-[72px] flex-shrink-0 ${
                      callMode ? "text-slate-500" : "text-slate-400"
                    }`}>Insurer</span>
                    <span className={`text-[13px] font-medium ${
                      callMode ? "text-slate-200" : "text-slate-700"
                    }`}>{caseData.insurance_company}</span>
                  </div>
                )}
                {caseData.denial_reason && (
                  <div className={`flex items-baseline gap-2 px-3.5 py-2.5 ${
                    callMode ? "bg-red-500/[0.04]" : "bg-red-50/50"
                  }`}>
                    <span className={`text-[11px] font-semibold min-w-[72px] flex-shrink-0 ${
                      callMode ? "text-slate-500" : "text-slate-400"
                    }`}>Denied for</span>
                    <span className={`text-xs font-medium ${
                      callMode ? "text-red-300/80" : "text-red-600"
                    }`}>{caseData.denial_reason}</span>
                  </div>
                )}
              </div>
            )}

            {/* Audio source row with waveform accent + privacy badge */}
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-2">
                {/* Mini EQ waveform bars */}
                <div className="flex items-end gap-[1.5px] h-3">
                  {[
                    { anim: "eqBar1", delay: "0s", h: 5, op: 0.5 },
                    { anim: "eqBar2", delay: "0.15s", h: 9, op: 0.7 },
                    { anim: "eqBar3", delay: "0.3s", h: 12, op: 0.9 },
                    { anim: "eqBar2", delay: "0.45s", h: 7, op: 0.6 },
                    { anim: "eqBar1", delay: "0.6s", h: 4, op: 0.4 },
                  ].map((bar, i) => (
                    <div
                      key={i}
                      className={`w-[2px] rounded-[1px] ${callMode ? "bg-teal-400" : "bg-teal-500"}`}
                      style={{
                        height: bar.h,
                        opacity: bar.op,
                        animation: `${bar.anim} 1.2s ease-in-out infinite ${bar.delay}`,
                      }}
                    />
                  ))}
                </div>
                <span className={`text-xs font-medium ${callMode ? "text-slate-400" : "text-slate-500"}`}>
                  Audio source
                </span>
              </div>
              <div 
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${
                  callMode 
                    ? "bg-emerald-500/10 border border-emerald-500/20" 
                    : "bg-emerald-50 border border-emerald-200"
                }`}
                title="Audio is analyzed in real time and not stored."
              >
                <IconShieldCheck className={`w-3 h-3 ${callMode ? "text-emerald-400" : "text-emerald-600"}`} />
                <span className={`text-[11px] font-medium ${callMode ? "text-emerald-300" : "text-emerald-700"}`}>
                  Not stored
                </span>
              </div>
            </div>

            {/* Audio controls — matched width, centered */}
            <div className="flex flex-col items-center gap-2.5">
              {/* Segmented toggle — teal active */}
              <div className={`flex rounded-lg p-0.5 w-full max-w-[340px] ${
                callMode 
                  ? "bg-slate-900/60 border border-slate-700/50" 
                  : "bg-slate-100 border border-slate-200"
              }`}>
                <button
                  onClick={() => setSelectedAudioSource("phone")}
                  className={`flex-1 flex items-center justify-center gap-2.5 py-3 rounded-md text-sm font-semibold transition-all ${
                    selectedAudioSource === "phone"
                      ? callMode
                        ? "bg-teal-500/10 text-teal-300 border border-teal-500/25 shadow-sm"
                        : "bg-teal-50 text-teal-700 border border-teal-200 shadow-sm"
                      : callMode
                        ? "text-slate-500 hover:text-slate-400 border border-transparent"
                        : "text-slate-400 hover:text-slate-500 border border-transparent"
                  }`}
                >
                  <IconPhone className="w-4 h-4" />
                  Phone Call
                </button>
                <button
                  onClick={() => setSelectedAudioSource("computer")}
                  className={`flex-1 flex items-center justify-center gap-2.5 py-3 rounded-md text-sm font-semibold transition-all ${
                    selectedAudioSource === "computer"
                      ? callMode
                        ? "bg-teal-500/10 text-teal-300 border border-teal-500/25 shadow-sm"
                        : "bg-teal-50 text-teal-700 border border-teal-200 shadow-sm"
                      : callMode
                        ? "text-slate-500 hover:text-slate-400 border border-transparent"
                        : "text-slate-400 hover:text-slate-500 border border-transparent"
                  }`}
                >
                  <IconComputer className="w-4 h-4" />
                  Computer Audio
                </button>
              </div>

              {/* Start button — matched width */}
              <button
                onClick={() => startListening(selectedAudioSource)}
                disabled={isConnecting}
                className={`w-full max-w-[340px] flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 ${
                  callMode
                    ? "bg-gradient-to-r from-teal-600 to-cyan-600 text-white hover:from-teal-500 hover:to-cyan-500"
                    : "bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-400 hover:to-cyan-400"
                }`}
                style={{
                  boxShadow: callMode
                    ? "0 0 20px rgba(20,184,166,0.25), 0 4px 12px rgba(20,184,166,0.15)"
                    : "0 0 16px rgba(20,184,166,0.2), 0 4px 10px rgba(20,184,166,0.15)"
                }}
              >
                <IconArrowRight className="w-4 h-4" />
                Start P2P Call
              </button>
            </div>

            {/* Call tuning — collapsed summary row */}
            <div className={`mt-3 border-t ${callMode ? "border-slate-700/50" : "border-slate-100"}`}>
              <button
                onClick={() => setShowCallTuning(!showCallTuning)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 transition-colors ${
                  callMode
                    ? "hover:bg-slate-800/50 text-slate-400"
                    : "hover:bg-slate-50 text-slate-500"
                }`}
              >
                <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.506-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-xs font-medium flex-1 text-left">
                  Call tuning
                </span>
                <svg className={`w-3 h-3 flex-shrink-0 transition-transform duration-200 ${
                  showCallTuning ? "rotate-180" : ""
                } ${callMode ? "text-slate-600" : "text-slate-400"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>

              {showCallTuning && (
                <div className="px-3.5 pb-2.5 flex flex-col gap-1">
                  {/* Key facts */}
                  {showKeyFactsInput ? (
                    <div className={`rounded-lg border p-3 ${
                      callMode ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50"
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <label className={`text-xs font-medium ${callMode ? "text-slate-300" : "text-slate-600"}`}>
                          Key facts to cite
                        </label>
                        <button
                          onClick={() => setShowKeyFactsInput(false)}
                          className={`text-[10px] font-medium transition-colors ${
                            callMode ? "text-slate-500 hover:text-slate-400" : "text-slate-400 hover:text-slate-500"
                          }`}
                        >
                          Done
                        </button>
                      </div>
                      <textarea
                        autoFocus
                        value={keyFacts}
                        onChange={(e) => handleKeyFactsChange(e.target.value)}
                        placeholder="e.g., Pain 8/10 for 6 weeks, failed PT x 8 sessions"
                        rows={2}
                        className={`w-full px-3 py-2 rounded-lg border text-xs resize-none focus:outline-none ${
                          callMode 
                            ? "bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500 focus:border-teal-500" 
                            : "bg-white border-slate-200 text-slate-700 placeholder-slate-400 focus:border-teal-500"
                        }`}
                      />
                    </div>
                  ) : keyFacts.trim() ? (
                    <div className="flex items-center gap-2 py-1.5">
                      <IconCheck className={`w-3 h-3 flex-shrink-0 ${callMode ? "text-teal-400" : "text-teal-600"}`} />
                      <span className={`text-xs truncate flex-1 min-w-0 ${callMode ? "text-slate-500" : "text-slate-400"}`}>
                        {keyFacts.trim()}
                      </span>
                      <button
                        onClick={() => setShowKeyFactsInput(true)}
                        className={`text-[11px] font-medium flex-shrink-0 transition-colors ${
                          callMode ? "text-teal-400 hover:text-teal-300" : "text-teal-600 hover:text-teal-700"
                        }`}
                      >
                        Edit
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowKeyFactsInput(true)}
                      className={`flex items-center gap-2 w-full py-1.5 transition-colors ${
                        callMode 
                          ? "text-slate-500 hover:text-slate-400" 
                          : "text-slate-400 hover:text-slate-500"
                      }`}
                    >
                      <IconPlus className="w-3 h-3" />
                      <span className="text-xs">Add key facts</span>
                    </button>
                  )}

                  {/* Keywords */}
                  {showKeywordsField ? (
                    <div className={`rounded-lg border p-3 ${
                      callMode ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50"
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <label className={`text-xs font-medium ${callMode ? "text-slate-300" : "text-slate-600"}`}>
                            Keywords to recognize
                          </label>
                          {selectedCount > 0 && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              callMode ? "bg-slate-700 text-slate-400" : "bg-slate-200 text-slate-500"
                            }`}>
                              {selectedCount}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => setShowKeywordsField(false)}
                          className={`text-[10px] font-medium transition-colors ${
                            callMode ? "text-slate-500 hover:text-slate-400" : "text-slate-400 hover:text-slate-500"
                          }`}
                        >
                          Done
                        </button>
                      </div>
                      
                      {keywordSuggestions.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2.5">
                          {keywordSuggestions.map((chip) => {
                            const isSelected = selectedKeywords.has(chip.value);
                            return (
                              <button
                                key={chip.id}
                                onClick={() => toggleKeyword(chip.value)}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                                  isSelected
                                    ? callMode 
                                      ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40'
                                      : 'bg-teal-100 text-teal-700 border border-teal-300'
                                    : callMode
                                      ? 'bg-slate-700 text-slate-400 border border-slate-600 hover:border-slate-500'
                                      : 'bg-white text-slate-500 border border-slate-300 hover:border-slate-400'
                                }`}
                              >
                                {isSelected && <IconCheck className="w-3 h-3" />}
                                {chip.value}
                                <span className={`text-[9px] px-1 rounded ${
                                  isSelected 
                                    ? callMode ? 'bg-teal-500/30 text-teal-400' : 'bg-teal-200 text-teal-600'
                                    : callMode ? 'bg-slate-600 text-slate-500' : 'bg-slate-200 text-slate-400'
                                }`}>
                                  {chip.source === 'medication' ? 'Rx' : 
                                   chip.source === 'procedure' ? 'Proc' :
                                   chip.source === 'study' ? 'Study' :
                                   chip.source === 'diagnosis' ? 'Dx' : ''}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      
                      {customKeywords.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2.5">
                          {customKeywords.map((keyword) => (
                            <span
                              key={keyword}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                                callMode 
                                  ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                                  : "bg-amber-100 text-amber-700 border border-amber-300"
                              }`}
                            >
                              {keyword}
                              <button
                                onClick={() => removeCustomKeyword(keyword)}
                                className={`transition-colors ${
                                  callMode ? "hover:text-amber-100" : "hover:text-amber-900"
                                }`}
                              >
                                <IconX className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <input
                          ref={customKeywordInputRef}
                          type="text"
                          value={customKeyword}
                          onChange={(e) => setCustomKeyword(e.target.value)}
                          onKeyDown={handleCustomKeywordKeyDown}
                          placeholder="Add a keyword..."
                          className={`flex-1 px-2.5 py-1.5 rounded-lg border text-sm focus:outline-none ${
                            callMode 
                              ? "bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500 focus:border-teal-500" 
                              : "bg-white border-slate-200 text-slate-700 placeholder-slate-400 focus:border-teal-500"
                          }`}
                        />
                        <button
                          onClick={addCustomKeyword}
                          disabled={!customKeyword.trim()}
                          className={`px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                            callMode 
                              ? "bg-slate-700 text-slate-300 hover:bg-slate-600" 
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          <IconPlus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : selectedCount > 0 ? (
                    <div className="flex items-center gap-2 py-1.5">
                      <IconCheck className={`w-3 h-3 flex-shrink-0 ${callMode ? "text-teal-400" : "text-teal-600"}`} />
                      <span className={`text-xs flex-1 ${callMode ? "text-slate-500" : "text-slate-400"}`}>
                        {selectedCount} keyword{selectedCount !== 1 ? "s" : ""} added
                      </span>
                      <button
                        onClick={handleRevealKeywords}
                        className={`text-[11px] font-medium flex-shrink-0 transition-colors ${
                          callMode ? "text-teal-400 hover:text-teal-300" : "text-teal-600 hover:text-teal-700"
                        }`}
                      >
                        Edit
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleRevealKeywords}
                      className={`flex items-center gap-2 w-full py-1.5 transition-colors ${
                        callMode 
                          ? "text-slate-500 hover:text-slate-400" 
                          : "text-slate-400 hover:text-slate-500"
                      }`}
                    >
                      <IconPlus className="w-3 h-3" />
                      <span className="text-xs">Add keywords</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Connecting */}
        {isConnecting && (
          <div className="flex flex-col items-center justify-center py-12">
            <IconLoader className="w-10 h-10 text-teal-400 mb-4" />
            <p className={`text-sm ${callMode ? "text-slate-400" : "text-slate-500"}`}>
              Connecting...
            </p>
          </div>
        )}

        {/* Listening */}
        {isListening && !isConnecting && (
          <div className="space-y-4">
            <div className={`p-4 rounded-lg ${callMode ? "bg-slate-700/30" : "bg-slate-50"}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {isPaused ? (
                    <>
                      <div className="w-2 h-2 bg-amber-500 rounded-full" />
                      <span className={`text-xs font-medium ${callMode ? "text-amber-400" : "text-amber-600"}`}>
                        PAUSED
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className={`text-xs font-medium ${callMode ? "text-green-400" : "text-green-600"}`}>
                        LISTENING
                      </span>
                    </>
                  )}
                </div>
                {!isPaused && (
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${callMode ? "text-slate-500" : "text-slate-400"}`}>Audio</span>
                    <div className={`w-20 h-2 rounded-full overflow-hidden ${callMode ? "bg-slate-600" : "bg-slate-200"}`}>
                      <div 
                        className="h-full bg-green-500 transition-all duration-75"
                        style={{ width: `${audioLevel * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
              {/* Reviewer authority / guided step strip */}
              {statusBarSlot}
              <p className={`text-sm text-center ${callMode ? "text-slate-400" : "text-slate-500"}`}>
                {isPaused 
                  ? "Listening paused. Click play to resume."
                  : audioLevel > 0.05 
                    ? "Transcribing — tactical cues will appear when relevant" 
                    : "Waiting for audio..."}
              </p>
            </div>
          </div>
        )}

        {/* Call ended */}
        {callEnded && (
          <div className="space-y-4">
            <div className={`p-4 rounded-lg text-center ${callMode ? "bg-slate-700/30" : "bg-slate-50"}`}>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3 ${
                callMode ? "bg-slate-600/50" : "bg-slate-200"
              }`}>
                <IconCheck className={`w-4 h-4 ${callMode ? "text-green-400" : "text-green-600"}`} />
                <span className={`text-sm font-medium ${callMode ? "text-slate-300" : "text-slate-600"}`}>
                  Call Ended {"\u2022"} {formatTime(elapsedTime)}
                </span>
              </div>
              {hasTranscript ? (
                <p className={`text-sm ${callMode ? "text-slate-400" : "text-slate-500"}`}>
                  Ready to generate an appeal letter based on your conversation.
                </p>
              ) : (
                <p className={`text-sm ${callMode ? "text-slate-400" : "text-slate-500"}`}>
                  No conversation detected during this session.
                </p>
              )}
            </div>

            {hasTranscript && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${callMode ? "text-slate-300" : "text-slate-600"}`}>
                  Additional context <span className={callMode ? "text-slate-500" : "text-slate-400"}>(optional)</span>
                </label>
                <textarea
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder="Add any details to strengthen the appeal - prior treatments, lab values, clinical notes..."
                  rows={3}
                  className={`w-full px-3 py-2 rounded-lg text-sm resize-none transition-colors ${
                    callMode 
                      ? "bg-slate-700/50 border border-slate-600 text-slate-200 placeholder-slate-500 focus:border-teal-500 focus:ring-1 focus:ring-teal-500" 
                      : "bg-white border border-slate-200 text-slate-700 placeholder-slate-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                  }`}
                />

                <div className="mt-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.txt"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={`flex items-center gap-2 text-sm ${
                      callMode 
                        ? "text-slate-400 hover:text-slate-300" 
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    <IconPaperclip className="w-4 h-4" />
                    Attach supporting documents
                  </button>

                  {uploadedFiles.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {uploadedFiles.map((file, index) => (
                        <div 
                          key={index}
                          className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                            callMode ? "bg-slate-700/50" : "bg-slate-100"
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <IconFileText className={`w-4 h-4 flex-shrink-0 ${callMode ? "text-slate-400" : "text-slate-500"}`} />
                            <span className={`truncate ${callMode ? "text-slate-300" : "text-slate-600"}`}>
                              {file.name}
                            </span>
                            <span className={`text-xs ${callMode ? "text-slate-500" : "text-slate-400"}`}>
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className={`p-1 rounded hover:bg-red-500/20 ${
                              callMode ? "text-slate-400 hover:text-red-400" : "text-slate-400 hover:text-red-500"
                            }`}
                          >
                            <IconX className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {appealError && (
              <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <IconAlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-400">Appeal generation failed</p>
                  <p className="text-sm text-red-300/70">{appealError}</p>
                </div>
              </div>
            )}

            <div className="flex justify-center gap-3">
              <button 
                onClick={reset} 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                  callMode ? "bg-slate-700 text-slate-300 hover:bg-slate-600" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <IconMicrophone className="w-4 h-4" /> Start New Call
              </button>
              {hasTranscript && (
                <button
                  onClick={handleGenerateClick}
                  disabled={generatingAppeal}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-teal-500 text-white hover:bg-teal-400 disabled:opacity-50 transition-all"
                >
                  {generatingAppeal ? (
                    <>
                      <IconLoader className="w-4 h-4" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <IconDocument className="w-4 h-4" />
                      Generate Appeal Letter
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom dock (e.g. QuickTap objection chips) */}
      {bottomSlot && (
        <div className={`border-t ${callMode ? "border-slate-700/60" : "border-slate-200"}`}>
          {bottomSlot}
        </div>
      )}

      {/* Appeal Letter Modal */}
      {showAppealModal && appealLetter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-2xl max-h-[80vh] flex flex-col rounded-xl shadow-2xl ${
            callMode ? "bg-slate-800" : "bg-white"
          }`}>
            <div className={`flex items-center justify-between p-4 border-b ${
              callMode ? "border-slate-700" : "border-slate-200"
            }`}>
              <div className="flex items-center gap-2">
                <IconDocument className={`w-5 h-5 ${callMode ? "text-teal-400" : "text-teal-600"}`} />
                <h3 className={`font-semibold ${callMode ? "text-white" : "text-slate-800"}`}>
                  Appeal Letter
                </h3>
              </div>
              <button
                onClick={() => setShowAppealModal(false)}
                className={`p-2 rounded-lg transition-colors ${
                  callMode ? "hover:bg-slate-700 text-slate-400" : "hover:bg-slate-100 text-slate-500"
                }`}
              >
                <IconX className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className={`whitespace-pre-wrap text-sm leading-relaxed ${
                callMode ? "text-slate-300" : "text-slate-700"
              }`}>
                {appealLetter}
              </div>
            </div>

            <div className={`flex items-center justify-end gap-3 p-4 border-t ${
              callMode ? "border-slate-700" : "border-slate-200"
            }`}>
              <button
                onClick={() => setShowAppealModal(false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  callMode ? "text-slate-400 hover:text-slate-300" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Close
              </button>
              <button
                onClick={copyAppealLetter}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  appealCopied 
                    ? "bg-green-500/20 text-green-400" 
                    : "bg-teal-500 text-white hover:bg-teal-400"
                }`}
              >
                {appealCopied ? (
                  <>
                    <IconCheck className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <IconCopy className="w-4 h-4" />
                    Copy Letter
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Strengthen Appeal Modal */}
      <StrengthenAppealModal
        isOpen={showStrengthenModal}
        onClose={() => setShowStrengthenModal(false)}
        onGenerate={handleGenerateWithDetails}
        onSkip={handleSkipStrengthen}
      />
    </div>
  );
}

export default CallTranscription;